/**
 * Form "Aggiungi esercizio" per Dry Fire e Live Fire.
 *
 * Progettato sull'ordine in cui un coach di tiro pensa quando prescrive, non
 * sull'elenco dei campi del modello. Le domande sono quattro, e il form le fa
 * una per volta:
 *
 *   1. **Che tipo di lavoro è?**   strutturato (misurabile) o libero (descritto)
 *   2. **Con cosa e su cosa?**      arma, focus, nome del drill
 *   3. **Quanto?**                  ripetizioni e par time, o serie e colpi
 *   4. **Come lo giudico?**         sistema di punteggio, obiettivo
 *
 * Tre scelte che vengono dall'uso, non dall'estetica:
 *
 * - **Il formato libero esiste** perché un lavoro come "5 minuti di messa a
 *   fuoco sulle mire" non ha serie né bersagli, e il form strutturato lo
 *   rendeva semplicemente non prescrivibile.
 * - **I campi rari stanno dietro "Altri dettagli".** Nell'80% dei drill servono
 *   nome, arma, volume e bersagli: chiedere sempre anche settore, note di setup
 *   e note per l'atleta è ciò che rendeva il form una parete di campi. Le
 *   condizioni di partenza invece sono rimaste in chiaro: «carica in fondina,
 *   mani sopra le spalle» e «già in mano» sono due esercizi diversi, non un
 *   dettaglio dello stesso.
 * - **Il riepilogo in fondo è sempre visibile** e mostra la riga che vedrà
 *   l'atleta, più **cosa manca** per poter salvare: il bottone «Aggiungi»
 *   spento senza una parola era il motivo per cui «non mi dice che manca il
 *   nome».
 *
 * Le sezioni «Quanto» e «Stage» sono **condivise con la libreria drill**
 * (`SeriesPlanSection`, `StageSetupSection`): i due form devono comportarsi
 * uguali, e il modo di garantirlo è che siano lo stesso codice.
 *
 * Spec: docs/shooting/04-frontend-ux.md §1 · docs/shooting/15-seconda-sessione-federico.md
 */
import { useEffect, useRef, useState } from "react";
import { BookOpen, ChevronDown, ChevronRight, Lock } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Textarea } from "./Textarea";
import { fetchAPI } from "../../lib/utils";
import {
	SHOOTING_FOCUS_OPTIONS,
	formatShootingPrescription,
	formatShootingTargets,
} from "../../lib/programBuilderUtils";
import { firearmTypeIcon } from "../shooting/weaponIcons";
import {
	StageDesignerDialog,
	StagePreview,
} from "../shooting/StageDesignerDialog";
import {
	Field,
	ScoringSystemZones,
	Section,
	StartConditionsSection,
} from "../shooting/formPrimitives";
import {
	DryFireQuantityFields,
	SeriesPlanSection,
} from "../shooting/SeriesPlanSection";
import {
	StageSetupSection,
	stageImageSrc,
} from "../shooting/StageSetupSection";
import { ImageZoomDialog } from "../shooting/ImageZoomDialog";
import { shootingMissingFields } from "../shooting/shootingPlan";

const FIREARM_TYPE_OPTIONS = [
	{ value: "handgun", labelKey: "pbShootFirearmHandgun" },
	{ value: "rifle", labelKey: "pbShootFirearmRifle" },
	{ value: "shotgun", labelKey: "pbShootFirearmShotgun" },
	{ value: "pcc", labelKey: "pbShootFirearmPcc" },
];

/**
 * Il punto d'ingresso dalla libreria, **in evidenza**.
 *
 * Era una tendina stretta sopra il form, facile da non vedere: «più evidente
 * per prendere gli esercizi già fatti». Qui è un riquadro con il suo titolo,
 * il conteggio dei drill e la tendina a tutta larghezza — e si vede anche
 * quando la libreria è vuota, con la strada per riempirla.
 */
function DrillLibraryPicker({ drills, onPick, t }) {
	return (
		<div className="rounded-sm border border-volt/40 bg-volt/[0.07] p-3 space-y-2">
			<div className="flex items-start gap-2">
				<BookOpen className="w-4 h-4 text-volt shrink-0 mt-0.5" />
				<div className="min-w-0">
					<p className="text-xs font-semibold uppercase tracking-wider text-volt">
						{t("pbShootLibraryPickerTitle")}
					</p>
					<p className="text-[11px] text-muted-foreground">
						{drills.length > 0
							? `${drills.length} ${t("pbShootLibraryCount")} · ${t("pbShootLibraryPickerHint")}`
							: t("pbShootLibraryEmptyHint")}
					</p>
				</div>
			</div>
			{drills.length > 0 && (
				<Select value="" onValueChange={onPick}>
					<SelectTrigger className="bg-background border-volt/30 rounded-sm h-11 text-sm">
						<SelectValue placeholder={t("pbShootPickFromLibrary")} />
					</SelectTrigger>
					<SelectContent className="bg-card border-border">
						{drills.map((drill) => (
							<SelectItem key={drill.drill_id} value={drill.drill_id}>
								{drill.name}
								{drill.library_scope === "global"
									? ` · ${t("pbShootStandardSuffix")}`
									: ""}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			)}
		</div>
	);
}

/**
 * I campi di un drill di libreria: **si leggono, non si scrivono**.
 *
 * Il drill è già configurato nella libreria — nome, arma, volume, punteggio,
 * condizioni di partenza — e modificarne la copia nel giorno lo faceva
 * divergere in silenzio dal drill che dice di essere. I campi restano dove
 * sono, leggibili, ma spenti: `disabled` li spegne a vista e `inert` toglie
 * anche il tocco alle tendine, che si aprono sul pointerdown.
 */
function LockedFields({ locked, children }) {
	return (
		<fieldset
			disabled={locked}
			inert={locked}
			className="min-w-0 m-0 p-0 border-0 [&_:disabled]:!opacity-80 [&_:disabled]:!cursor-default"
		>
			{children}
		</fieldset>
	);
}

/** Il riquadro che dice perché i campi sono spenti, e come riaccenderli. */
function LibraryLockBanner({ name, onUnlink, t }) {
	return (
		<div className="rounded-sm border border-volt/40 bg-volt/[0.07] px-3 py-2.5 flex items-start gap-2">
			<Lock className="w-4 h-4 text-volt shrink-0 mt-0.5" />
			<div className="min-w-0 flex-1 space-y-0.5">
				<p className="text-sm font-medium break-words">
					{name || t("pbShootFromLibrary")}
				</p>
				<p className="text-[11px] text-muted-foreground leading-snug">
					{t("pbShootLibraryLockedHint")}
				</p>
			</div>
			<Button
				type="button"
				variant="outline"
				size="sm"
				className="rounded-sm h-8 text-xs shrink-0"
				onClick={onUnlink}
				title={t("pbShootUnlinkHint")}
			>
				{t("pbShootUnlink")}
			</Button>
		</div>
	);
}

export function ShootingForm({
	mode,
	newExercise,
	setNewExercise,
	suggestedFirearmType = null,
	t,
}) {
	const [scoringSystems, setScoringSystems] = useState([]);
	const [drills, setDrills] = useState([]);
	const [detailsOpen, setDetailsOpen] = useState(false);
	// La piantina del drill di libreria si apre in sola lettura, a schermo
	// pieno; l'immagine si ingrandisce.
	const [designerOpen, setDesignerOpen] = useState(false);
	const [zoomSrc, setZoomSrc] = useState(null);

	const isDryFire = mode === "dry_fire";
	const shooting = newExercise.shooting || {};
	const isFree = shooting.prescription_format === "free";

	useEffect(() => {
		let cancelled = false;
		Promise.all([
			isDryFire
				? Promise.resolve({})
				: fetchAPI("/shooting/reference").catch(() => ({})),
			fetchAPI(`/shooting/drills?mode=${mode}`).catch(() => []),
		]).then(([reference, drillList]) => {
			if (cancelled) return;
			setScoringSystems(reference?.scoring_systems || []);
			setDrills(Array.isArray(drillList) ? drillList : []);
		});
		return () => {
			cancelled = true;
		};
	}, [mode, isDryFire]);

	const patch = (changes) =>
		setNewExercise({
			...newExercise,
			shooting: { ...shooting, mode, ...changes },
		});

	const patchExercise = (changes) =>
		setNewExercise({
			...newExercise,
			...changes,
			shooting: { ...shooting, mode },
		});

	const targets = Array.isArray(shooting.targets) ? shooting.targets : [];
	const selectedSystem = scoringSystems.find(
		(system) => system.system_id === shooting.scoring_system_id,
	);
	// Il drill viene dalla libreria: bersagli e piantina sono **già
	// configurati lì** e qui si consultano. Modificarli su una copia farebbe
	// divergere in silenzio l'esercizio dal drill che dice di essere.
	const fromLibrary = !!shooting.drill_id;
	const linkedDrill = drills.find((d) => d.drill_id === shooting.drill_id);
	const targetsLine = formatShootingTargets(targets, t);
	const hasDrawnStage =
		(shooting.stage_layout || []).length > 0 ||
		targets.some((target) => target.x_m !== null && target.x_m !== undefined);
	const libraryStageSrc = stageImageSrc(shooting.stage_image_url);
	const WeaponIcon = firearmTypeIcon(shooting.firearm_type || "handgun");
	const missing = shootingMissingFields({
		...newExercise,
		exercise_type: mode,
		shooting: { ...shooting, mode },
	});

	// L'arma della sessione: se il coach sta programmando una giornata di
	// pistola, il prossimo esercizio nasce già con la pistola. Suggerimento,
	// non vincolo — resta modificabile, e si applica solo a campo vuoto.
	const didSuggest = useRef(false);
	useEffect(() => {
		if (didSuggest.current) return;
		if (!suggestedFirearmType || shooting.firearm_type) return;
		didSuggest.current = true;
		patch({ firearm_type: suggestedFirearmType });
		// `patch` si ricostruisce a ogni render: dipenderci farebbe girare
		// l'effetto a vuoto.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [suggestedFirearmType, shooting.firearm_type]);

	// Applicare un drill riempie la prescrizione; le note dell'esercizio no —
	// quelle sono del giorno, il drill è il modello. Le ripetizioni del dry
	// fire vivono su `sets`, fuori dalla prescrizione: si copiano.
	const applyDrill = (drillId) => {
		const drill = drills.find((d) => d.drill_id === drillId);
		if (!drill) return;
		const prescription = drill.prescription || {};
		setNewExercise({
			...newExercise,
			exercise_name: drill.name,
			sets:
				mode === "dry_fire" && prescription.reps_planned
					? String(prescription.reps_planned)
					: newExercise.sets,
			shooting: {
				...prescription,
				mode,
				drill_id: drill.drill_id,
				// Il settore è della giornata, non del drill: resta quello
				// scritto qui (i drill salvati prima potevano averne uno).
				stage_sector: shooting.stage_sector || null,
			},
		});
	};

	const summary = formatShootingPrescription({
		...newExercise,
		shooting: { ...shooting, mode },
	});

	return (
		<div className="space-y-3">
			{/* ── 1. CHE TIPO DI LAVORO È ── */}
			<div className="grid grid-cols-2 gap-1 bg-secondary p-1 rounded-sm">
				{[
					{
						value: "structured",
						labelKey: "pbShootFormatStructured",
						hintKey: "pbShootFormatStructuredHint",
					},
					{
						value: "free",
						labelKey: "pbShootFormatFree",
						hintKey: "pbShootFormatFreeHint",
					},
				].map((option) => (
					<button
						key={option.value}
						type="button"
						onClick={() => patch({ prescription_format: option.value })}
						className={`flex flex-col items-center py-2 rounded-sm text-xs font-medium transition-colors ${
							(shooting.prescription_format || "structured") === option.value
								? "bg-volt text-white"
								: "text-muted-foreground hover:text-foreground"
						}`}
					>
						{t(option.labelKey)}
						<span className="text-[10px] opacity-70">{t(option.hintKey)}</span>
					</button>
				))}
			</div>

			{isFree ? (
				/* ── FORMATO LIBERO ──
				   Un lavoro come "5 minuti di messa a fuoco sulle mire" non ha
				   serie né bersagli: chiedere nome, cosa fare e durata è tutto
				   quello che serve. */
				<>
					<Field label={t("titleLabel")}>
						<Input
							value={newExercise.exercise_name || ""}
							onChange={(e) => patchExercise({ exercise_name: e.target.value })}
							className="bg-secondary border-transparent rounded-sm h-11"
							placeholder={t("pbShootFreeTitlePlaceholder")}
						/>
					</Field>
					<Field label={t("pbShootWhatToDo")}>
						<Textarea
							value={newExercise.notes || ""}
							onChange={(e) => patchExercise({ notes: e.target.value })}
							className="bg-secondary border-transparent rounded-sm"
							rows={4}
							placeholder={t("pbShootWhatToDoPlaceholder")}
						/>
					</Field>
					<div className="grid grid-cols-2 gap-2">
						<Field label={t("durationMin")}>
							<Input
								type="number"
								inputMode="numeric"
								value={shooting.duration_minutes ?? ""}
								onChange={(e) =>
									patch({
										duration_minutes: e.target.value
											? parseInt(e.target.value, 10)
											: null,
									})
								}
								className="bg-secondary border-transparent rounded-sm h-11"
								placeholder="5"
							/>
						</Field>
						<div className="space-y-1">
							<Label className="label-tactical flex items-center gap-1.5">
								<WeaponIcon size={13} /> {t("pbShootFirearm")}
							</Label>
							<Select
								value={shooting.firearm_type || "__none__"}
								onValueChange={(v) =>
									patch({ firearm_type: v === "__none__" ? null : v })
								}
							>
								<SelectTrigger className="bg-secondary border-transparent rounded-sm h-11">
									<SelectValue placeholder="—" />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									<SelectItem value="__none__">{t("pbShootFirearmUnset")}</SelectItem>
									{FIREARM_TYPE_OPTIONS.map((ft) => (
										<SelectItem key={ft.value} value={ft.value}>
											{t(ft.labelKey)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
				</>
			) : (
				/* ── FORMATO STRUTTURATO ── */
				<>
					<DrillLibraryPicker drills={drills} onPick={applyDrill} t={t} />

					{fromLibrary && (
						<LibraryLockBanner
							name={linkedDrill?.name || newExercise.exercise_name}
							onUnlink={() => patch({ drill_id: null })}
							t={t}
						/>
					)}

					{/* ── 2. CON COSA ── */}
					<LockedFields locked={fromLibrary}>
					<Section title={t("pbShootDrillSection")}>
						<Field label={t("pbShootDrillName")}>
							<Input
								value={newExercise.exercise_name || ""}
								onChange={(e) =>
									patchExercise({ exercise_name: e.target.value })
								}
								className="bg-background border-transparent rounded-sm h-11"
								placeholder={
									isDryFire ? "Es: Estrazione a par time" : "Es: El Presidente"
								}
							/>
						</Field>
						<div className="grid grid-cols-2 gap-2">
							<Field label={t("pbShootFirearm")}>
								<Select
									value={shooting.firearm_type || "__none__"}
									onValueChange={(v) =>
										patch({ firearm_type: v === "__none__" ? null : v })
									}
								>
									<SelectTrigger className="bg-background border-transparent rounded-sm">
										<span className="flex items-center gap-1.5">
											<WeaponIcon size={13} />
											<SelectValue placeholder={t("pbShootFirearm")} />
										</span>
									</SelectTrigger>
									<SelectContent className="bg-card border-border">
										<SelectItem value="__none__">
											{t("pbShootFirearmUnsetLong")}
										</SelectItem>
										{FIREARM_TYPE_OPTIONS.map((ft) => (
											<SelectItem key={ft.value} value={ft.value}>
												{t(ft.labelKey)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</Field>
							<Field label={t("pbShootFocus")}>
								<Select
									value={shooting.drill_focus || "__none__"}
									onValueChange={(v) =>
										patch({ drill_focus: v === "__none__" ? null : v })
									}
								>
									<SelectTrigger className="bg-background border-transparent rounded-sm">
										<SelectValue placeholder={t("pbShootFocus")} />
									</SelectTrigger>
									<SelectContent className="bg-card border-border">
										<SelectItem value="__none__">
											{t("pbShootFocusUnset")}
										</SelectItem>
										{SHOOTING_FOCUS_OPTIONS.map((f) => (
											<SelectItem key={f.value} value={f.value}>
												{t(f.labelKey)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</Field>
						</div>
					</Section>
					</LockedFields>

					{/* ── 3. QUANTO ── */}
					<LockedFields locked={fromLibrary}>
					{isDryFire ? (
						<DryFireQuantityFields
							reps={newExercise.sets}
							onRepsChange={(value) => patchExercise({ sets: value })}
							parTime={shooting.par_time_seconds}
							onParTimeChange={(value) => patch({ par_time_seconds: value })}
							t={t}
						/>
					) : (
						<SeriesPlanSection shooting={shooting} onChange={patch} t={t} />
					)}
					</LockedFields>

					{/* ── STAGE ── */}
					{fromLibrary ? (
						/* Drill di libreria: bersagli e piantina si **consultano**.
						   Sono configurati nella libreria, e due vie d'uscita
						   esplicite valgono più di un campo modificabile che fa
						   divergere la copia dall'originale. */
						<Section
							title={t("pbShootStageSection")}
							hint={t("pbShootFromLibraryHint")}
						>
							{targetsLine && (
								<p className="text-xs">
									<span className="text-muted-foreground">
										{t("pbShootTargets")}:{" "}
									</span>
									{targetsLine}
								</p>
							)}
							{libraryStageSrc && (
								<button
									type="button"
									className="w-full"
									onClick={() => setZoomSrc(libraryStageSrc)}
									title={t("pbShootZoom")}
								>
									<img
										src={libraryStageSrc}
										alt={t("pbShootStageMap")}
										className="w-full max-h-40 object-contain rounded-sm bg-background"
									/>
								</button>
							)}
							{hasDrawnStage && (
								<StagePreview
									prescription={shooting}
									onOpen={() => setDesignerOpen(true)}
								/>
							)}
							{(shooting.attachments || []).length > 0 && (
								<div className="grid grid-cols-3 gap-1.5">
									{shooting.attachments.map((item, index) => (
										<button
											key={`${item.url}_${index}`}
											type="button"
											className="rounded-sm border border-border bg-background overflow-hidden text-left"
											onClick={() => setZoomSrc(stageImageSrc(item.url))}
										>
											<img
												src={stageImageSrc(item.url)}
												alt={item.label || ""}
												className="w-full h-16 object-contain bg-white"
											/>
											{item.label && (
												<p className="text-[10px] text-muted-foreground px-1.5 py-1 truncate">
													{item.label}
												</p>
											)}
										</button>
									))}
								</div>
							)}
							<div className="flex gap-1.5">
								{hasDrawnStage && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										className="rounded-sm h-7 text-xs flex-1"
										onClick={() => setDesignerOpen(true)}
									>
										{t("pbShootViewStage")}
									</Button>
								)}
							</div>
							<StageDesignerDialog
								open={designerOpen}
								onOpenChange={setDesignerOpen}
								prescription={shooting}
								plannedRounds={shooting.rounds_planned}
								readOnly
							/>
							<ImageZoomDialog
								open={!!zoomSrc}
								onOpenChange={(open) => !open && setZoomSrc(null)}
								src={zoomSrc}
								title={t("pbShootStageMap")}
							/>
						</Section>
					) : (
						<StageSetupSection shooting={shooting} onChange={patch} t={t} />
					)}

					{/* ── CONDIZIONI DI PARTENZA ──
					    In chiaro, non dietro «altri dettagli»: descrivono
					    l'esercizio quanto i bersagli e il par time. */}
					<LockedFields locked={fromLibrary}>
						<StartConditionsSection shooting={shooting} onChange={patch} t={t} />
					</LockedFields>

					{/* ── 4. COME LO GIUDICO ── */}
					{/* Un drill di libreria senza sistema di punteggio non si
					    salverebbe: lì la tendina resta aperta, l'unica. */}
					{!isDryFire && (
						<LockedFields locked={fromLibrary && !!shooting.scoring_system_id}>
						<Section
							title={t("pbShootHowJudged")}
							hint={t("pbShootHowJudgedHint")}
						>
							<Field label={t("pbShootScoringSystem")}>
								<Select
									value={shooting.scoring_system_id || "__none__"}
									onValueChange={(v) =>
										patch({ scoring_system_id: v === "__none__" ? null : v })
									}
								>
									<SelectTrigger className="bg-background border-transparent rounded-sm h-11">
										<SelectValue placeholder={t("pbShootScoringSystem")} />
									</SelectTrigger>
									<SelectContent className="bg-card border-border">
										<SelectItem value="__none__">{t("noneOption")}</SelectItem>
										{scoringSystems.map((system) => (
											<SelectItem key={system.system_id} value={system.system_id}>
												{system.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</Field>
							{selectedSystem ? (
								<ScoringSystemZones system={selectedSystem} />
							) : (
								/* Obbligatorio: senza, il risultato non è calcolabile e
								   il salvataggio è bloccato a monte, in ProgramBuilder. */
								<p className="text-xs text-amber-400">
									{t("pbShootNoScoringWarning")}
								</p>
							)}
						</Section>
						</LockedFields>
					)}

					{/* ── 5. COSA DEVE OTTENERE, E COSA DEVE REGISTRARE ──
					    Erano un campo solo ("criterio di riuscita") usato per tre
					    scopi diversi. Il terzo è quello che mancava davvero: su un
					    esercizio di brandeggio in cui si segna solo lo split,
					    l'istruzione finiva nelle note e l'atleta registrava la cosa
					    sbagliata. */}
					<LockedFields locked={fromLibrary}>
					<Section title={t("pbShootGoalSection")}>
						<Field
							label={t("pbShootAthleteGoal")}
							hint={t("pbShootAthleteGoalHint")}
						>
							<Input
								value={shooting.athlete_goal ?? shooting.success_criteria ?? ""}
								onChange={(e) =>
									patch({ athlete_goal: e.target.value, success_criteria: null })
								}
								className="bg-background border-transparent rounded-sm"
								placeholder={
									isDryFire
										? t("pbShootSuccessCriteriaDry")
										: t("pbShootSuccessCriteriaLive")
								}
							/>
						</Field>
						<Field
							label={t("pbShootScoringInstructions")}
							hint={t("pbShootScoringInstructionsHint")}
						>
							<Input
								value={shooting.scoring_instructions || ""}
								onChange={(e) => patch({ scoring_instructions: e.target.value })}
								className="bg-background border-transparent rounded-sm"
								placeholder={t("pbShootScoringInstructionsPlaceholder")}
							/>
						</Field>
						<Field
							label={t("pbShootCoachNotes")}
							hint={t("pbShootCoachNotesHint")}
						>
							<Textarea
								value={shooting.coach_notes || ""}
								onChange={(e) => patch({ coach_notes: e.target.value })}
								className="bg-background border-transparent rounded-sm"
								rows={2}
								placeholder={t("pbShootCoachNotesPlaceholder")}
							/>
						</Field>
					</Section>
					</LockedFields>

					{fromLibrary ? (
						/* ── PER QUESTA GIORNATA ──
						   Le due cose che un drill di libreria non può sapere: quale
						   parte dell'allestimento di oggi usa, e cosa dire
						   all'atleta oggi. Sono le uniche che restano aperte. */
						<Section
							title={t("pbShootDaySection")}
							hint={t("pbShootDaySectionHint")}
						>
							{shooting.target_setup && (
								<LockedFields locked>
									<Field
										label={t("pbShootSetupNotes")}
										hint={t("pbShootSetupNotesHint")}
									>
										<Input
											value={shooting.target_setup}
											readOnly
											className="bg-background border-transparent rounded-sm"
										/>
									</Field>
								</LockedFields>
							)}
							<Field
								label={t("pbShootStageSector")}
								hint={t("pbShootStageSectorHint")}
							>
								<Input
									value={shooting.stage_sector || ""}
									onChange={(e) => patch({ stage_sector: e.target.value })}
									className="bg-background border-transparent rounded-sm"
									placeholder="Es: settore A, bersagli 1-3"
								/>
							</Field>
							<Field
								label={t("pbShootAthleteNotes")}
								hint={t("pbShootAthleteNotesHint")}
							>
								<Textarea
									value={newExercise.notes || ""}
									onChange={(e) => patchExercise({ notes: e.target.value })}
									className="bg-background border-transparent rounded-sm"
									rows={2}
									placeholder={t("pbShootAthleteNotes")}
								/>
							</Field>
						</Section>
					) : (
						<>
					{/* ── ALTRI DETTAGLI ──
					    Campi che nell'80% dei drill restano vuoti: chiederli sempre
					    è ciò che rendeva il form una parete. */}
					<button
						type="button"
						onClick={() => setDetailsOpen((open) => !open)}
						className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
					>
						{detailsOpen ? (
							<ChevronDown className="w-3.5 h-3.5" />
						) : (
							<ChevronRight className="w-3.5 h-3.5" />
						)}
						{t("pbShootOtherDetails")}
					</button>

					{detailsOpen && (
						<div className="space-y-2.5 pl-1">
							<Field
								label={t("pbShootSetupNotes")}
								hint={t("pbShootSetupNotesHint")}
							>
								<Input
									value={shooting.target_setup || ""}
									onChange={(e) => patch({ target_setup: e.target.value })}
									className="bg-secondary border-transparent rounded-sm"
									placeholder={t("pbShootSetupNotes")}
								/>
							</Field>
							<Field
								label={t("pbShootStageSector")}
								hint={t("pbShootStageSectorHint")}
							>
								<Input
									value={shooting.stage_sector || ""}
									onChange={(e) => patch({ stage_sector: e.target.value })}
									className="bg-secondary border-transparent rounded-sm"
									placeholder="Es: settore A, bersagli 1-3"
								/>
							</Field>
							{/* Un campo note e uno solo: il messaggio all'atleta per
							    la giornata. Le note del coach stanno nella sezione
							    "obiettivo e registrazione". */}
							<Field
								label={t("pbShootAthleteNotes")}
								hint={t("pbShootAthleteNotesHint")}
							>
								<Textarea
									value={newExercise.notes || ""}
									onChange={(e) => patchExercise({ notes: e.target.value })}
									className="bg-secondary border-transparent rounded-sm"
									rows={2}
									placeholder={t("pbShootAthleteNotes")}
								/>
							</Field>
						</div>
					)}
						</>
					)}
				</>
			)}

			{/* ── RIEPILOGO ──
			    La riga che vedrà l'atleta, mentre la compili, e cosa manca per
			    poter salvare. Il bottone «Aggiungi» spento senza una parola era
			    il difetto: qui la parola c'è. */}
			<div className="rounded-sm border border-volt/25 bg-volt/[0.06] px-3 py-2 space-y-1">
				<p className="text-[10px] uppercase tracking-wider text-muted-foreground">
					{t("pbShootAthleteWillSee")}
				</p>
				<p className="text-sm">
					<span
						className={`font-semibold ${isDryFire ? "text-sky-400" : "text-red-400"}`}
					>
						{isDryFire ? "Dry fire" : "Live fire"}
					</span>
					{newExercise.exercise_name ? ` · ${newExercise.exercise_name}` : ""}
					{summary ? (
						<span className="text-muted-foreground"> · {summary}</span>
					) : (
						<span className="text-muted-foreground"> · {t("pbShootToFill")}</span>
					)}
				</p>
				{missing.length > 0 && (
					<p className="text-[11px] text-amber-400">
						{t("pbShootMissing")}:{" "}
						{missing
							.map((key) =>
								key === "name"
									? t("pbShootMissingName")
									: t("pbShootMissingScoring"),
							)
							.join(" · ")}
					</p>
				)}
			</div>
		</div>
	);
}
